// campaign-edit-copy.ts — what the tier-2 campaign editor TELLS the user (#103).
//
// Pure, so it is the one part of this feature that is actually testable here
// (jest is `testEnvironment: 'node'` — no DOM, no React Testing Library). The
// modal renders whatever this returns and decides nothing itself.
//
// TWO RULES DRIVE EVERY LINE BELOW.
//
// 1. RENDER FROM THE BOOLEANS, NOT ONLY FROM `warnings[]`.
//    `warnings[]` is ready-to-render server copy and is always shown verbatim —
//    but it is NOT complete. Known gap in the backend (`stillPausedWarning`'s
//    'sequence' branch is unreachable): a commit on a drip-step campaign whose
//    PARENT SEQUENCE the user paused returns
//    `{campaignResumed: true, dripResumed: false, warnings: []}`. Rendering
//    warnings alone would say "Sending has resumed" while the sequence is
//    stopped and no later step will ever go out. So every sentence here is
//    derived from `campaignResumed` / `dripResumed` / `catchUpRan`, and the
//    warnings are appended.
//
// 2. NEVER CLAIM MORE THAN HAPPENED.
//    - A mid-flight edit yields a MIXED audience. `generateCampaignMessage`
//      copies `campaign.template` at DISPATCH time, so recipients whose message
//      was already minted keep the old body. "All remaining recipients get the
//      new text" is false and this file never says it.
//    - "Resume sending" (`campaign-edit-abort {recover: true}`) is offered ONLY
//      where it can actually work: on a campaign pause carrying an edit-session
//      marker. Recovery deliberately REFUSES a pause the user took themselves,
//      so offering the button there would be a false remedy — the mistake an
//      earlier round made by pointing at a "campaign list" control that does
//      not exist.
//    - A stuck SEQUENCE is not offered that button: the recovery abort only
//      matches an edit-marked drip pause, and the sequence's own menu already
//      has a working Resume (`useDripActions` `pauseResume`). The copy points
//      there instead.

import type {
    CampaignEditBeginResult,
    CampaignEditFinishResult,
    PendingCampaignEditResult,
} from '@common-models/campaign-edit';
import { isTerminalCampaignStatus } from './campaign-editability';

/**
 * The narrow slice of i18next's `t` this module needs.
 *
 * Passed IN rather than imported: these functions are pure and node-testable
 * (that is the whole reason this file exists), and a module-level `t` would
 * either drag i18next into a node suite or resolve against whatever locale
 * happened to be loaded. Required, never optional — an optional `t` is how a
 * new call site quietly starts rendering English again.
 */
export type EditCopyT = (key: string, defaultValue: string) => string;

export type EditBannerTone = 'info' | 'success' | 'warning' | 'error';

/** Which call produced a finish result. `campaign-edit-abort` returns
 *  `templateSaved: false` by construction, so without this the recovery path
 *  would render "your changes were not saved" for an operation that never had
 *  changes to save. */
export type EditFinishOperation = 'commit' | 'recover';

/**
 * Everything `finishBanner` needs that the finish result itself does not carry.
 *
 * An OBJECT, not positional booleans, deliberately: `finishBanner(f, 'recover',
 * true, true)` is unreadable, and mixing up two adjacent unlabelled booleans is
 * precisely how the false "your changes were not saved" claim got shipped.
 */
export interface FinishBannerContext {
    operation: EditFinishOperation;
    /** `begin.pausedForEdit` — whether an edit-session marker was on the pause
     *  at all. It is what makes a leftover pause recoverable. */
    pauseWasEditOwned: boolean;
    /**
     * Did a commit EARLIER IN THIS EDITOR SESSION save the template?
     *
     * `campaign-edit-abort` always returns `templateSaved: false` (BE
     * `campaign-edit-actions.ts`), so the finish result alone cannot tell the
     * two contexts the recovery button appears in apart:
     *   (a) recover from the begin banner, form still on screen — the edit
     *       really is being discarded;
     *   (b) recover AFTER a commit that saved — the template IS saved, and
     *       saying otherwise contradicts the "Message updated." the user just
     *       read and drops a mixed-audience disclosure that is still true.
     * The modal knows which; only it can supply this.
     */
    templateSavedEarlier?: boolean;
}

export interface EditBanner {
    tone: EditBannerTone;
    /** Rendered in order, each its own sentence. Derived from the booleans. */
    lines: string[];
    /** The server's own sentences, verbatim and unmodified, after `lines`. */
    warnings: string[];
    /** Render the "Resume sending" control (abort with `recover: true`). */
    showResumeSending: boolean;
}

/** The disclosure that a mid-flight edit splits the audience across two message
 *  bodies. Stated as what is true, not as a reassurance. */
export const mixedAudienceLine = (t: EditCopyT): string => t(
    'campaignsRedesign.editCopy.mixedAudience',
    'Recipients who were already sent this campaign keep the previous message — only recipients not yet sent will get the new one.',
);

/** The sequence's own Resume lives in the sequence menu and works; this editor's
 *  recovery button deliberately does not touch a user-owned sequence pause. */
const sequenceStillPausedLine = (t: EditCopyT): string => t(
    'campaignsRedesign.editCopy.sequenceStillPaused',
    'The sequence is still paused, so no later steps will be sent. Resume it from the sequence menu to continue.',
);

/**
 * The banner while the editor is open, from `campaign-edit-begin`.
 *
 * `null` until begin returns (the modal renders nothing rather than guessing).
 */
export function beginBanner(begin: CampaignEditBeginResult | null, t: EditCopyT): EditBanner | null {
    if (!begin) return null;

    const lines: string[] = [];
    let tone: EditBannerTone = 'info';

    if (!begin.paused) {
        // The guarded pause did not apply — the campaign is not `running|active`
        // any more. `campaign-edit-commit` guards the template write on
        // `campaignStatus: 'paused'`, so a save here CANNOT land. Say that
        // rather than letting the user type into a form that will refuse them.
        lines.push(t('campaignsRedesign.editCopy.couldNotPause', 'This campaign could not be paused for editing, so changes cannot be saved right now. It may have already finished sending.'));
        tone = 'warning';
    } else if (begin.ownsPause) {
        lines.push(t('campaignsRedesign.editCopy.pausedWhileEditing', 'Campaign paused while you edit. It resumes when you save.'));
    } else if (begin.pausedForEdit) {
        // Someone else's edit session holds the pause — possibly an editor that
        // never came back. This is the stranded-campaign entry point, and the
        // only place in the product that can undo it.
        lines.push(t('campaignsRedesign.editCopy.pausedByOtherSession', 'This campaign is paused by another editing session, so saving here will not resume sending.'));
        tone = 'warning';
    } else {
        // A pause this editor did not take and does not own. Left exactly as the
        // user set it — deliberately, and not recoverable from this button.
        lines.push(t('campaignsRedesign.editCopy.alreadyPausedByUser', 'This campaign is already paused. Saving here will not resume sending — it stays exactly as you set it.'));
        tone = 'warning';
    }

    if (begin.isDripStep && begin.dripPaused) {
        lines.push(begin.ownsDripPause
            ? t('campaignsRedesign.editCopy.sequencePausedToo', 'The sequence is paused too, and resumes when you save.')
            : t('campaignsRedesign.editCopy.sequenceStaysPaused', 'The sequence was already paused and stays paused when you save.'));
    }

    return {
        tone,
        lines,
        warnings: begin.warnings ?? [],
        showResumeSending: canRecoverCampaignPause(begin.pausedForEdit, begin.campaignStatus)
            // While THIS session owns the pause, Save and Cancel are the
            // affordances — a third button that resumes without saving would
            // just be a confusing duplicate of Cancel.
            && !begin.ownsPause,
    };
}

/**
 * The banner after a commit (Save) or a recovery abort (Resume sending).
 *
 * CONTRACT GAP: `CampaignEditFinishResult` carries neither `pausedForEdit` nor
 * any record of an earlier save, which is why `FinishBannerContext` exists.
 * `pauseWasEditOwned` is necessarily begin's snapshot, taken when the editor
 * opened: if the marker was cleared server-side in between, the button is
 * offered once and does nothing. That is bounded here — a recovery that comes
 * back with the campaign still paused withdraws the button (see
 * `recoveryRefused`). Closing it fully needs `pausedForEdit` on the finish
 * result — recorded for 4a, not worked around client-side.
 */
export function finishBanner(
    finish: CampaignEditFinishResult,
    { operation, pauseWasEditOwned, templateSavedEarlier = false }: FinishBannerContext,
    t: EditCopyT,
): EditBanner {
    const lines: string[] = [];
    // The template is saved if THIS call saved it, or if a commit earlier in
    // this editor session did. A recovery never saves, but it does not unsave.
    const templateIsSaved = finish.templateSaved || templateSavedEarlier;

    if (operation === 'commit') {
        lines.push(finish.templateSaved
            ? t('campaignsRedesign.editCopy.messageUpdated', 'Message updated.')
            : t('campaignsRedesign.editCopy.notSavedNoLongerPaused', 'Your changes were not saved — this campaign was no longer paused for editing.'));
    } else if (!templateSavedEarlier) {
        // Recovery from the begin banner, with the form still on screen: it
        // resumes without saving, and it is offered exactly where the user is
        // most likely to have typed. Say so rather than letting the edit vanish
        // silently. NOT said when a commit already saved — see
        // `templateSavedEarlier`; claiming it there would contradict the
        // "Message updated." the user just read.
        lines.push(t('campaignsRedesign.editCopy.notSavedResumeOnly', 'Your changes were not saved — “Resume sending” only restarts sending.'));
    }

    lines.push(campaignLine(finish, t));

    if (finish.isDripStep) {
        lines.push(sequenceLine(finish, t));
    }

    if (templateIsSaved) {
        lines.push(mixedAudienceLine(t));
    }

    // A recovery that came back with the campaign STILL paused means the resume
    // did not apply, so this button has nothing more to offer. State ONLY that:
    // WHY it did not apply is not observable from here — the likely cause is a
    // pause with no edit marker, but another `begin` could equally have
    // re-paused the campaign during this round trip.
    const recoveryRefused = operation === 'recover'
        && !finish.campaignResumed
        && finish.campaignStatus === 'paused';
    if (recoveryRefused) {
        lines.push(t('campaignsRedesign.editCopy.pauseNotLiftable', 'This pause cannot be lifted from here.'));
    }

    const somethingStuck = !finish.campaignResumed || (finish.isDripStep && !finish.dripResumed);
    const tone: EditBannerTone = (operation === 'commit' && !finish.templateSaved)
        ? 'error'
        : somethingStuck ? 'warning' : 'success';

    return {
        tone,
        lines,
        warnings: finish.warnings ?? [],
        showResumeSending: !recoveryRefused && canRecoverCampaignPause(pauseWasEditOwned, finish.campaignStatus),
    };
}

/**
 * What happened to the CAMPAIGN, from its status — not from `campaignResumed`
 * alone.
 *
 * `campaignResumed` is `campaignStatus === 'running'`: plain STATE, not "we
 * transitioned it". So `false` also covers a campaign that FINISHED or was
 * aborted while the editor was open (the last already-minted message completing
 * mid-edit is an ordinary occurrence). Printing "still paused" there is simply
 * untrue, and it points at a recovery the campaign does not need.
 */
function campaignLine(finish: CampaignEditFinishResult, t: EditCopyT): string {
    if (finish.campaignResumed) return t('campaignsRedesign.editCopy.sendingResumed', 'Sending has resumed.');
    const status = finish.campaignStatus;
    if (status === 'paused') return t('campaignsRedesign.editCopy.sendingStillPaused', 'Sending is still paused.');
    if (status === 'aborted') return t('campaignsRedesign.editCopy.campaignStopped', 'This campaign was stopped, so nothing was resumed.');
    if (isTerminalCampaignStatus(status)) return t('campaignsRedesign.editCopy.campaignFinished', 'This campaign finished sending — there was nothing left to resume.');
    return t('campaignsRedesign.editCopy.sendingNotResumed', 'Sending has not resumed.');
}

/** Same reasoning for the parent sequence: `dripResumed` is
 *  `dripStatus === 'active'`, so `false` covers a sequence someone completed or
 *  aborted in another tab — where pointing at the sequence menu's Resume would
 *  be a remedy that does not apply. */
function sequenceLine(finish: CampaignEditFinishResult, t: EditCopyT): string {
    if (finish.dripResumed) {
        return finish.catchUpRan
            ? t('campaignsRedesign.editCopy.sequenceRunningCaughtUp', 'The sequence is running again, and the step that finished while it was paused has been started.')
            : t('campaignsRedesign.editCopy.sequenceRunning', 'The sequence is running again.');
    }
    const status = finish.dripStatus;
    if (status === 'paused') return sequenceStillPausedLine(t);
    if (status === 'aborted') return t('campaignsRedesign.editCopy.sequenceStopped', 'The sequence was stopped, so no later steps will be sent.');
    if (status === 'completed') return t('campaignsRedesign.editCopy.sequenceCompleted', 'The sequence has finished — no later steps are left to send.');
    // `null` is what the backend reports when it could not READ the drip
    // (`loadDrip` swallows a transient error and returns undefined), not a
    // status meaning "stopped". An unknown must not be rendered as a definite
    // negative — that would tell the user their sequence is not running when
    // nobody actually knows.
    if (!status) return t('campaignsRedesign.editCopy.sequenceUnknown', 'The sequence could not be read, so whether later steps will be sent is unknown.');
    return t('campaignsRedesign.editCopy.sequenceNotRunning', 'The sequence is not running, so no later steps will be sent.');
}

/**
 * Can `campaign-edit-abort {recover: true}` actually resume this campaign?
 *
 * Only if it is paused AND the pause carries an edit-session marker: the
 * recovery guard is `{campaignStatus: 'paused', pausedForEditBy: {$ne: null}}`,
 * so an unmarked (user-owned) pause matches nothing and the button would be a
 * button that provably cannot work.
 */
function canRecoverCampaignPause(pausedForEdit: boolean, campaignStatus: string | null): boolean {
    return Boolean(pausedForEdit) && campaignStatus === 'paused';
}

// ── The SCHEDULED-campaign editor (#103 tier 1, standalone) ───────────────
//
// A different editor with a different failure, so a different set of sentences.
// Tier 2 above is about a PAUSE — who owns it, whether it lifted. This one has
// no pause at all: a scheduled campaign is not dispatching, so it is edited
// directly, and the single thing that can go wrong is that the dispatcher
// STARTED it while the user was typing.
//
// That refusal is the sentence that matters most in this whole feature, and it
// has three obligations. It must say the changes are GONE (they are: the save is
// one compare-and-set, refused whole); it must say WHY in terms of something the
// user saw happen ("it started sending"), not in terms of a status token; and it
// must hand over the tool that still works. "Edit message" is that tool while
// the campaign is running — and is NOT, once it has finished, which is why the
// terminal branch below points nowhere instead of pointing wrong.

/** What the editor is reporting. `error` is a FAILED CALL (network/HTTP), which
 *  is categorically different from a refusal: a refusal is the server telling us
 *  the campaign moved on, an error is us not knowing anything. */
export type ScheduledEditOutcome =
    | { kind: 'saved' }
    | { kind: 'refused'; campaignStatus: string | null }
    | { kind: 'error' };

/** The server answers `saved` or not; nothing here is inferred. */
export const scheduledEditOutcome = (result: PendingCampaignEditResult): ScheduledEditOutcome =>
    result.saved ? { kind: 'saved' } : { kind: 'refused', campaignStatus: result.campaignStatus };

/** Pointer to the tier-2 editor, by the exact label of the menu item that opens
 *  it — a remedy the user cannot find is not a remedy. */
const editMessageRemedy = (t: EditCopyT): string => t(
    'campaignsRedesign.editCopy.editMessageRemedy',
    'You can still change the message for anyone it has not reached yet — close this and choose “Edit message” from the campaign menu.',
);

export function scheduledEditBanner(outcome: ScheduledEditOutcome, t: EditCopyT): EditBanner {
    if (outcome.kind === 'saved') {
        return {
            tone: 'success',
            lines: [
                t('campaignsRedesign.editCopy.scheduledSaved', 'Your changes are saved.'),
                // Worth stating, because it is the difference from the tier-2
                // editor the user may have used before: nothing has gone out, so
                // there is no mixed audience to disclose.
                t('campaignsRedesign.editCopy.scheduledSavedAll', 'This campaign has not started sending, so everyone in the audience will get the new message.'),
            ],
            warnings: [],
            showResumeSending: false,
        };
    }

    if (outcome.kind === 'error') {
        return {
            tone: 'error',
            // Says what is known and nothing more. A failed call is not evidence
            // the campaign changed, so it must not claim the edit was refused.
            lines: [t('campaignsRedesign.editCopy.scheduledError', 'Your changes could not be saved. Nothing was changed — try again.')],
            warnings: [],
            showResumeSending: false,
        };
    }

    const status = outcome.campaignStatus;
    const lines = [
        t('campaignsRedesign.editCopy.scheduledRefusedStarted', 'Your changes were not saved — this campaign started sending while you were editing it.'),
        t('campaignsRedesign.editCopy.scheduledRefusedStartedWhy', 'Once a campaign starts, its audience and its send time are locked in, so the whole edit was refused rather than half-applied.'),
    ];
    if (status === 'aborted') {
        lines[0] = t('campaignsRedesign.editCopy.scheduledRefusedStopped', 'Your changes were not saved — this campaign was stopped while you were editing it.');
        lines[1] = t('campaignsRedesign.editCopy.scheduledRefusedStoppedWhy', 'A stopped campaign will not send anything further, so there was nothing to change.');
    } else if (isTerminalCampaignStatus(status)) {
        lines[0] = t('campaignsRedesign.editCopy.scheduledRefusedFinished', 'Your changes were not saved — this campaign finished sending while you were editing it.');
        lines[1] = t('campaignsRedesign.editCopy.scheduledRefusedFinishedWhy', 'It has already reached everyone in its audience, so there is nothing left to change.');
    } else {
        // Still live: the message really can still be changed for the recipients
        // it has not reached. Offered only here, deliberately — pointing a
        // finished campaign at "Edit message" would send the user to an editor
        // that opens, round-trips and refuses.
        lines.push(editMessageRemedy(t));
    }

    return { tone: 'error', lines, warnings: [], showResumeSending: false };
}
